/**
 * Race engine API client. The only place the race workspace talks to the backend.
 * Replay files are immutable (addressed by build id) and cached by the browser.
 */
import { API } from "../lib/api";

const BASE = `${API}/race-engine`;

async function getJson(url, { signal, timeoutMs = 15000 } = {}) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);
    const onAbort = () => controller.abort();
    if (signal) signal.addEventListener("abort", onAbort);
    try {
        let response;
        try {
            response = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
        } catch (error) {
            // A request the caller abandoned (superseded seek, unmounted panel) is not a data failure; a timeout is.
            if (error?.name === "AbortError" && timedOut) {
                const err = new Error(`Timeout after ${timeoutMs} ms for ${url}`);
                err.name = "TimeoutError";
                err.timeout = true;
                throw err;
            }
            throw error;
        }
        if (!response.ok) {
            let detail = null;
            try {
                detail = await response.json();
            } catch (error) {
                detail = null;
            }
            const err = new Error(`HTTP ${response.status} for ${url}`);
            err.status = response.status;
            err.detail = detail;
            throw err;
        }
        return await response.json();
    } finally {
        clearTimeout(timer);
        if (signal) signal.removeEventListener("abort", onAbort);
    }
}

export const raceEngineApi = {
    sessions: (opts) => getJson(`${BASE}/sessions`, opts),
    session: (sessionKey, opts) => getJson(`${BASE}/sessions/${sessionKey}`, opts),
    layout: (sessionKey, opts) => getJson(`${BASE}/sessions/${sessionKey}/layout`, opts),
    manifest: (sessionKey, opts) => getJson(`${BASE}/sessions/${sessionKey}/replay/manifest`, opts),
    events: (sessionKey, opts) => getJson(`${BASE}/sessions/${sessionKey}/replay/events`, opts),
    replayFile: (sessionKey, buildId, path, opts) => getJson(`${BASE}/sessions/${sessionKey}/replay/${buildId}/${path}`, opts),
    serverSnapshot: (sessionKey, t, opts) => getJson(`${BASE}/sessions/${sessionKey}/replay/snapshot?t=${encodeURIComponent(t)}`, opts),
    live: (opts) => getJson(`${BASE}/live`, { ...opts, timeoutMs: 8000 }),
    liveStreamUrl: (since) => `${BASE}/live/stream${since != null ? `?since=${since}` : ""}`,
};

export default raceEngineApi;
